import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import {
  Container,
  Button,
  Modal,
  Col,
  Row,
  Form,
  Table,
  Badge,
  CloseButton,
  Card,
} from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import Select from 'react-select';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ReceptionistManagement = () => {
  const [doctors, setDoctors] = useState([]);
  const [subHospitals, setSubHospitals] = useState([]);
  const [assignedDoctors, setAssignedDoctors] = useState([]);
  const [selectedReceptionist, setSelectedReceptionist] = useState('');
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [selectedSubHospital, setSelectedSubHospital] = useState('');
  const [allReceptionists, setAllReceptionists] = useState([]);
  const [subHospitalReceptionists, setSubHospitalReceptionists] = useState([]);
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);

  const hospitalId = localStorage.getItem('hospitalId');
  const token = localStorage.getItem('token');

  const showWarning = (message) => {
    toast(message, {
      icon: '⚠️',
      style: { background: '#fffbe6', color: '#664d03', border: '1px solid #ffecb5' },
    });
  };

  const handleRemoveDoctorConfirm = (doctorId, receptionistId) => {
    Swal.fire({
      title: 'Remove Doctor?',
      text: 'Are you sure you want to remove this doctor?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        handleRemoveDoctor(doctorId, receptionistId);
        Swal.fire('Removed!', 'Doctor has been removed.', 'success');
      }
    });
  };

  useEffect(() => {
    if (user?.hospital_id) {
      fetchSubHospitals(user.hospital_id);
      fetchReceptionistsByHospital(user.hospital_id);
      fetchAllAssignedDoctors(user.hospital_id);
    }
  }, [user]);

  const fetchSubHospitals = async (hospitalId) => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/subHospitals/getSubHospitalsByHospital/${hospitalId}`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      setSubHospitals(data);
    } catch {}
  };

  const fetchReceptionistsByHospital = async (hospitalId) => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/receptionist/getAllReceptionists`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      setAllReceptionists(data.receptionists.filter((r) => r.hospitalId === hospitalId));
    } catch {}
  };

  const fetchReceptionistsBySubHospital = async (hospitalId, subhospitalId) => {
    if (!hospitalId || !subhospitalId) return;
    try {
      const res = await fetch(
        `${BASE_URL}/api/receptionist/getReceptionistsBySubHospital?hospitalId=${hospitalId}&subhospitalId=${subhospitalId}`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      setSubHospitalReceptionists(data);
    } catch {}
  };

  const fetchAllAssignedDoctors = async (hospitalId) => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/receptionistDoctor/get-all-assigned-doctors/${hospitalId}`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      setAssignedDoctors(data);
    } catch {}
  };

  const fetchDoctorsBySubHospital = async (subHospitalId) => {
    if (!hospitalId || !subHospitalId) return;
    try {
      const res = await fetch(
        `${BASE_URL}/api/doctor/getAllDoctors?hospital_id=${hospitalId}&subhospital_id=${subHospitalId}`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      setDoctors(data);
    } catch {}
  };

  const handleAssignDoctors = async () => {
    if (!selectedReceptionist) return showWarning('Please select a receptionist!');
    const doctorIds = selectedDoctors.map((d) => d.value);
    if (doctorIds.length === 0) return showWarning('Please select at least one doctor.');
    try {
      const res = await fetch(
        `${BASE_URL}/api/receptionistDoctor/assign-multiple-doctors`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            receptionistId: selectedReceptionist,
            doctorIds,
            hospitalId: user.hospital_id,
            subHospitalId: selectedSubHospital || null,
          }),
        }
      );
      if (!res.ok) throw new Error();
      toast.success('Doctors assigned successfully!');
      setSelectedDoctors([]);
      fetchAllAssignedDoctors(user.hospital_id);
      setShowModal(false);
    } catch {}
  };

  const handleRemoveDoctor = async (doctorId, receptionistId) => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/receptionistDoctor/remove-doctor`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ receptionistId, doctorId }),
        }
      );
      if (!res.ok) throw new Error();
      setAssignedDoctors(
        assignedDoctors.filter(
          (d) => d.doctor_id !== doctorId || d.receptionist_id !== receptionistId
        )
      );
    } catch {}
  };

  const handleOpenModal = () => {
    setSelectedReceptionist('');
    setSelectedDoctors([]);
    setSelectedSubHospital('');
    setShowModal(true);
  };

  return (
    <div className="d-flex bg-light min-vh-100">
      <Sidebar className="d-none d-md-block" />
      <Container fluid className="py-4">
        {/* Heading and Button - Professional and Consistent */}
        <Row className="align-items-center mb-3">
          <Col>
            <h2 className="global-heading mb-0">
              Receptionist - Doctor Assignment
            </h2>
            <div className="text-muted fw-normal fs-6">
              Easily assign and manage doctors for each receptionist.
            </div>
          </Col>
          <Col xs="auto">
            <Button
              className="global-btn fw-normal d-flex align-items-center"
              size="sm"
              onClick={handleOpenModal}
            >
              {/* <span className="material-icons me-1" style={{ fontSize: 18 }}>
                add_circle
              </span> */}
              Manage Doctors
            </Button>
          </Col>
        </Row>
        <Card className="shadow-sm border-0 mb-4">
          <Card.Body>
            <Table responsive bordered hover className="mb-0 align-middle">
              <thead className="bg-primary text-white">
                <tr>
                  <th>Sr. No</th>
                  <th>Receptionist</th>
                  <th>Sub-Hospital</th>
                  <th>Doctors</th>
                </tr>
              </thead>
              <tbody>
                {allReceptionists.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-secondary">
                      No Receptionists Found
                    </td>
                  </tr>
                ) : (
                  allReceptionists.map((rec, index) => {
                    const subHospitalName =
                      rec.sub_hospital_name ||
                      (subHospitals.find(
                        (sub) => Number(sub.sub_hospital_id) === Number(rec.sub_hospital_id)
                      )?.sub_hospital_name ?? 'Unknown');
                    const doctorsForRec = assignedDoctors.filter(
                      (d) => d.receptionist_id === rec.id
                    );
                    return (
                      <tr key={rec.id}>
                        <td>{index + 1}</td>
                        <td className="fw-semibold">{rec.name}</td>
                        <td>{subHospitalName}</td>
                        <td>
                          {doctorsForRec.length > 0 ? (
                            <div className="d-flex flex-wrap gap-2">
                              {doctorsForRec.map((doc) => (
                                <Badge
                                  key={doc.doctor_id}
                                  pill
                                  bg="light"
                                  text="dark"
                                  className="border d-flex align-items-center"
                                >
                                  <span className="fw-semibold"
                                   style={{ color: 'var(--primary-color)' }}
                                   >{doc.doctor_name}</span>
                                  <CloseButton
                                    className="ms-2"
                                    variant="danger"
                                    aria-label="Remove"
                                    onClick={() => handleRemoveDoctorConfirm(doc.doctor_id, rec.id)}
                                  />
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-danger">No Doctors Assigned</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Modal for Assigning Doctors */}
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          centered
          backdrop="static"
          className="form-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="global-heading fs-5">
              Assign Doctors
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="mb-3">
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label
                    className="fw-semibold"
                    style={{ color: 'var(--primary-color)' }}>
                      Select Sub-Hospital</Form.Label>
                    <Form.Select
                      value={selectedSubHospital}
                      onChange={(e) => {
                        setSelectedSubHospital(e.target.value);
                        fetchDoctorsBySubHospital(e.target.value);
                        fetchReceptionistsBySubHospital(hospitalId, e.target.value);
                      }}
                    >
                      <option value="">Select Sub-Hospital</option>
                      {subHospitals.map((sh) => (
                        <option key={sh.sub_hospital_id} value={sh.sub_hospital_id}>
                          {sh.sub_hospital_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group>
                    <Form.Label
                    className="fw-semibold"
                    style={{ color: 'var(--primary-color)' }}>
                      Select Receptionist</Form.Label>
                    <Form.Select
                      value={selectedReceptionist}
                      onChange={(e) => setSelectedReceptionist(e.target.value)}
                    >
                      <option value="">Select Receptionist</option>
                      {subHospitalReceptionists.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              {selectedReceptionist && (
                <Form.Group>
                  <Form.Label 
                  className="fw-semibold"
                    style={{ color: 'var(--primary-color)' }}>
                    Assign Multiple Doctors
                  </Form.Label>
                  <Select
                    isMulti
                    options={doctors
                      .filter((d) =>
                        !assignedDoctors.some(
                          (ad) =>
                            String(ad.doctor_id) === String(d.id) &&
                            String(ad.receptionist_id) === String(selectedReceptionist)
                        )
                      )
                      .map((d) => ({ value: d.id, label: d.name }))}
                    onChange={setSelectedDoctors}
                    styles={{
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 1050,
                      }),
                    }}
                  />
                </Form.Group>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button
              variant="primary"
              onClick={handleAssignDoctors}
              className="global-btn w-100 fw-normal d-flex align-items-center justify-content-center"
              size="sm"
            >
              <span className="material-icons me-1" style={{ fontSize: 16 }}>
                check_circle
              </span>
              Assign Doctors
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default ReceptionistManagement;